import type { MutableRefObject } from 'react'
import React, { useEffect } from 'react'
import { Editor } from '@tinymce/tinymce-react'
import { createPortal, flushSync } from 'react-dom'
import { ErrorContent, ShowIf } from '@ztechapps/webui.util'
import { useTheme } from '@ztechapps/common.hook'
import type { InitOptions } from '@tinymce/tinymce-react/lib/cjs/main/ts/components/Editor'
import styles from './RichTextInput.module.scss'

export enum RichTextInputVariantEnum {
  Full = 'full',
  Compact = 'compact',
  Minimal = 'minimal',
}

export type RichTextInputProps = {
  value: string
  onChange: (value: string) => void
  height?: number | string
  apiKey?: string
  editorRef?: MutableRefObject<any>
  tinymceScriptSrc?: string
  disabled?: boolean
  init?: InitOptions
  variant?: RichTextInputVariantEnum
  /** Content portalled into the editor header before the formatting toolbar. Must keep one width once rendered. */
  toolbarStart?: React.ReactNode
  /** Content portalled into the editor header after the formatting toolbar. Must keep one width once rendered. */
  toolbarEnd?: React.ReactNode
}

type ToolbarSlots = { start: HTMLDivElement; end: HTMLDivElement }

const SHORTCUT_HINTS = 'Ctrl+B Bold · Ctrl+I Italic · Ctrl+U Underline · Ctrl+K Link · Ctrl+Z Undo · Ctrl+Y Redo'

export const RichTextInput = ({
  value,
  onChange,
  height = '80vh',
  apiKey,
  editorRef,
  tinymceScriptSrc = '/tinymce/tinymce.min.js',
  disabled,
  init,
  variant = RichTextInputVariantEnum.Full,
  toolbarStart,
  toolbarEnd,
}: RichTextInputProps) => {
  const [initialValue, setInitialValue] = React.useState(value)
  const _editorRef = React.useRef<any>(null)
  const safeEditorRef = editorRef ?? _editorRef

  const theme = useTheme()
  const { isDark } = theme.colorMode

  const skin = isDark ? 'oxide-dark' : 'oxide'
  const content_css = isDark ? 'dark' : 'default'

  useEffect(() => {
    setInitialValue(value)
  }, [isDark])

  const [scriptErrors, setScriptErrors] = React.useState(null)
  const [toolbarSlots, setToolbarSlots] = React.useState<ToolbarSlots | null>(null)
  const hasToolbarSlots = Boolean(toolbarStart || toolbarEnd)

  if (scriptErrors)
    return (
      <ErrorContent
        icon="paste"
        error={scriptErrors}
        message="There was an error loading the text editor. Please try to refresh the browser."
      />
    )

  const isMinimal = variant === RichTextInputVariantEnum.Minimal
  const isCompact = variant === RichTextInputVariantEnum.Compact
  const showMenubar = !isCompact && !isMinimal
  const showToolbar = !isMinimal

  // A single-element string array takes the theme's multi-toolbar branch, whose groups
  // render with no floating partition and therefore no overflow button; a plain string
  // builds the floating toolbar that collapses into "…". Slotted editors take the string
  // shape so the partition measures the width left between the slots; slotless editors
  // keep the array shape and render as before.
  const toolbarItems =
    'undo redo |  styles  | bold italic | alignleft aligncenter alignright | bullist numlist outdent indent | hr | help'
  const toolbar = showToolbar ? (hasToolbarSlots ? toolbarItems : [toolbarItems]) : false

  //We set the key here, to regenerate whenever the color mode changes, this forces to re-rerender the edito
  return (
    <>
      <Editor
        key={skin}
        tinymceScriptSrc={tinymceScriptSrc}
        onInit={(evt, editor) => {
          safeEditorRef.current = editor
        }}
        licenseKey="gpl"
        apiKey={apiKey}
        initialValue={initialValue}
        init={{
          relative_urls: false,
          remove_script_host: false,
          height,
          browser_spellcheck: true,
          contextmenu: false,
          menubar: showMenubar,
          toolbar,
          plugins: [
            'advlist',
            'anchor',
            'autolink',
            'charmap',
            'code',
            'fullscreen',
            'help',
            'image',
            'insertdatetime',
            'link',
            'lists',
            'media',
            'preview',
            'searchreplace',
            'table',
            'visualblocks',
            'accordion',
          ],
          content_style: `body { font-family: Lato,Inter,Roboto,Helvetica,Arial,sans-serif; font-size:13px }
          a {  color: unset important; background-color: unset !important; cursor: pointer; }

          `,
          skin,
          content_css,
          //merge the init props with the default props
          ...init,
          setup: (editor) => {
            init?.setup?.(editor)
            if (!hasToolbarSlots) return
            // Slots are created once per editor; the colour-mode remount builds a new
            // editor with a fresh closure, so a plain flag (not state) is enough.
            let slotsBuilt = false
            // setup runs in the Editor constructor before the theme renders the header, and editor
            // handlers fire in registration order, so this SkinLoaded handler runs before the theme's
            // own one builds the toolbar groups and partitions them against
            // .tox-toolbar__primary's offsetWidth; the theme re-measures only when the iframe's
            // innerWidth changes, which slot content never triggers. SkinLoaded fires from the
            // skin stylesheet's load promise, never inside a React commit, so flushing here is legal.
            editor.on('SkinLoaded', () => {
              if (slotsBuilt) return
              const container = editor.getContainer()
              const header = container.querySelector('.tox-editor-header')
              const overlord = header?.querySelector('.tox-toolbar-overlord')
              if (!header || !overlord) return
              slotsBuilt = true
              const start = container.ownerDocument.createElement('div')
              start.className = 'd-flex align-items-center px-1'
              const end = container.ownerDocument.createElement('div')
              // flex-shrink-0 (not flex: 0 0 auto — grow:0 and basis:auto are already the
              // browser defaults) keeps the end slot from being squeezed thin inside row 1;
              // with nothing left to shrink it wraps whole to a second header row instead.
              end.className = 'd-flex align-items-center px-1 flex-shrink-0'
              overlord.before(start)
              overlord.after(end)
              // Bootstrap utilities, not a module rule: their `!important` beats the skin's
              // own non-important `.tox-editor-header { display: grid; ... }` (skin.css:1866)
              // with no specificity trick needed. text-body re-points the header at the app
              // theme — the skin paints .tox in a fixed ink (#222f3e in oxide and oxide-dark
              // alike) and forces every descendant to `color: inherit`, so slot controls would
              // render dark-on-dark under the dark skin otherwise.
              header.classList.add('d-flex', 'flex-wrap', 'align-items-center', 'text-body')
              container.classList.add(styles.withToolbarSlots)
              // Commit the portal content synchronously so the slots below already hold
              // their final width when the theme's own handler runs later in this same
              // dispatch and takes its one partition measurement. No re-dispatch follows:
              // the string toolbar shape builds the floating toolbar directly, so the
              // theme's single pass already overflows into "…"; re-dispatching would only
              // rebuild the same toolbar a second time.
              flushSync(() => setToolbarSlots({ start, end }))
            })
          },
        }}
        onEditorChange={onChange}
        disabled={disabled}
        value={value}
        onScriptsLoadError={(e) => {
          setScriptErrors(e)
        }}
      />
      {toolbarSlots ? createPortal(toolbarStart, toolbarSlots.start) : null}
      {toolbarSlots ? createPortal(toolbarEnd, toolbarSlots.end) : null}
      <ShowIf condition={isMinimal}>
        <small className="text-secondary mt-1 d-block">{SHORTCUT_HINTS}</small>
      </ShowIf>
    </>
  )
}
